<template>
  <lf-modal
    :title="$t('BUSINESS_PROFILES.BUSINESS.CREATE_BSINESS_ENTITY')"
    :close="() => close(false)"
  >
    <template #content>
      <div class="p-6">
        <form @submit="onSubmit">
          <lf-h3 class="mr-2 mb-5">
            {{ $t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.TITLE") }}
          </lf-h3>
          <business-info-fields
            v-model:addresses="allAddresses"
            :telephone-errors="telephoneErrors"
            @primary-phone-id-updated="primaryPhoneId = $event"
          />
          <lf-h3 class="mr-2 mb-5">
            {{
              $t("BUSINESS_PROFILES.BUSINESS.ADDITIONAL_BUSINESS_INFO.TITLE")
            }}
          </lf-h3>
          <additional-business-info-fields
            @industry-type-change="onIndustryTypeChange"
            @website-address-change="handleWebsiteAddressChange"
          />
          <hr class="my-4" />
          <div class="flex justify-end">
            <modal-buttons
              class="bg-white"
              save-is-submit
              :disabled="isSubmitting"
              :on-cancel="() => close(false)"
            />
          </div>
        </form>
      </div>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { Entity, Relation } from "@/enums/entityProfiles";
import { filterOutEmptyAddresses } from "@/helpers/deals";
import {
  filterNotNullableValues,
  forceNullForEmptyStrings,
  getDateFromMonth,
} from "@/helpers/formatting";
import { getAsOfValue } from "@/helpers/relations";
import { callStoreAction } from "@/helpers/vee-validate";
import { websiteAddressChange } from "@/helpers/workflow";
import useBusinessesStore from "@/stores/businesses";
import useEntityRelationsStore from "@/stores/entityRelations";

import AdditionalBusinessInfoFields from "@/components/entityProfiles/cards/AdditionalBusinessInfoFields.vue";
import BusinessInfoFields from "@/components/entityProfiles/cards/BusinessInfoFields.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";

import type { IProfile } from "@/models/businesses";
import type { IAddress } from "@/models/common";
import type { RelationshipPayload } from "@/models/entityProfiles";
import type { IOptions } from "@/models/options";
import type { IRootState } from "@/models/state";
import type { PhoneNumber } from "@/models/workflows";
import type { PropType } from "vue";

const props = defineProps({
  close: {
    type: Function as PropType<(param?: boolean) => void>,
    required: true,
  },
  relationship: {
    type: Object as PropType<RelationshipPayload>,
  },
  mainEntityId: {
    type: String,
    default: "",
  },
  individualProfileId: {
    type: String as PropType<IProfile["id"]>,
  },
});

const telephoneErrors = ref<string[]>([]);
const primaryPhoneId = ref<PhoneNumber["id"]>(undefined);

const allAddresses = ref<IAddress[]>([]);

const { getters } = useStore<IRootState>();
const businessesStore = useBusinessesStore();
const { handleSubmit, isSubmitting, setFieldValue } = useForm();

const entityRelationsStore = useEntityRelationsStore();
const { latestOwnershipSnapshotOwners } = storeToRefs(entityRelationsStore);
const { createOwnershipSnapshot, addRelation } = entityRelationsStore;

const options = computed<IOptions>(() => getters["options/all"]);

const getFormattedPhones = (values: Record<string, string | unknown>) =>
  Object.entries(values).reduce<PhoneNumber[]>((result, [key, value]) => {
    if (!key.includes("phone_numbers_") || isEmpty(values[key])) {
      return result;
    }
    const phoneData: PhoneNumber = {};
    const id = Number(key.replace("phone_numbers_", ""));
    phoneData["phone_number"] = String(value);
    phoneData.is_primary = id === primaryPhoneId.value;
    result.push(phoneData);
    return result;
  }, []);

const onIndustryTypeChange = () => {
  setFieldValue("sub_industry_type_id", undefined);
};

const handleWebsiteAddressChange = (e: Event) => {
  setFieldValue("website_address", websiteAddressChange(e));
};

const onSubmit = handleSubmit(async (values, actions) => {
  telephoneErrors.value = [];
  let payload = cloneDeep(values);

  if (payload?.fiscal_year_end) {
    payload.fiscal_year_end = getDateFromMonth(payload.fiscal_year_end);
  }

  payload.phone_numbers = getFormattedPhones(payload);
  payload.addresses = filterOutEmptyAddresses(allAddresses.value);
  payload = filterNotNullableValues(payload, Object.keys(payload));
  payload = forceNullForEmptyStrings(payload);
  if (payload?.formation_state) {
    payload.formation_state =
      options.value?.states[payload.formation_state as string];
  }

  if (isEmpty(payload?.piis?.EIN) && isEmpty(payload?.piis?.BN)) {
    delete payload["piis"];
  }

  if (isEmpty(payload?.phone_numbers)) {
    delete payload["phone_numbers"];
  }

  payload.type = Entity.business;
  try {
    const response = await callStoreAction(
      payload,
      actions,
      "createEntityProfile",
      businessesStore
    );
    if (props.relationship && response.id) {
      await addRelation({
        profileId: props.mainEntityId,
        initiatingEntityType:
          props.individualProfileId &&
          props.relationship.type === Relation.owner
            ? Entity.individual
            : Entity.business,
        //don't update relationship if we're creating a relationship from individual entity profile
        updateRelationship:
          !props.individualProfileId ||
          props.relationship.type === Relation.owner,
        relationship: { ...props.relationship, profile_id: response.id },
      });
      if (props.relationship.type === "company") {
        const ownershipSnapshotPayload = {
          as_of: getAsOfValue(),
          owners: [
            ...latestOwnershipSnapshotOwners.value,
            { percentage: 0, profile_id: response.id },
          ],
        };
        await createOwnershipSnapshot({
          entityId: props.mainEntityId,
          entityType: Entity.business,
          data: ownershipSnapshotPayload,
        });
      }
    }
  } catch (error) {
    const errors = (error as { formattedErrors: Record<string, string> })
      .formattedErrors;
    Object.keys(errors).forEach((key) => {
      if (key.includes("phone_numbers")) {
        telephoneErrors.value.push(errors[key]);
      }
    });

    return;
  }
  props.close(true);
});
</script>
